import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Layout
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';

// Pagine pubbliche
import LoginPage from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Pagine protette
import DashboardPage from './pages/DashboardPage';

// Pagine Admin
import UsersPage from './pages/admin/UsersPage';
import UserFormPage from './pages/admin/UserFormPage';
import AssignmentsPage from './pages/admin/AssignmentsPage';

// Pagine Documenti
import DocumentsPage from './pages/documents/DocumentsPage';

// Pagine Impostazioni
import SettingsPage from './pages/settings/SettingsPage';

// Componente per proteggere le route
function ProtectedRoute({ children, allowedRoles }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // I clienti vanno ai documenti, gli altri alla dashboard
    const redirectPath = user.role === 'client' ? '/my-documents' : '/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
}

// Componente per redirect basato sul ruolo
function RoleBasedRedirect() {
  const { user } = useAuth();
  const redirectPath = user?.role === 'client' ? '/my-documents' : '/dashboard';
  return <Navigate to={redirectPath} replace />;
}

// Componente per redirect se già autenticato
function PublicRoute({ children }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // I clienti vanno direttamente ai documenti
    const redirectPath = user?.role === 'client' ? '/my-documents' : '/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
}

function App() {
  return (
    <Routes>
      {/* Route pubbliche */}
      <Route element={<AuthLayout />}>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password/:token"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />
      </Route>

      {/* Route protette */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Route Admin */}
        <Route
          path="/users"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/new"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UserFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/:id/edit"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UserFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/assignments"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AssignmentsPage />
            </ProtectedRoute>
          }
        />

        {/* Route Documenti - accessibile a tutti gli utenti autenticati */}
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/my-documents" element={<DocumentsPage />} />

        {/* Route Impostazioni - accessibile a tutti gli utenti autenticati */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/profile" element={<SettingsPage />} />

        {/* Redirect root in base al ruolo */}
        <Route path="/" element={<RoleBasedRedirect />} />
      </Route>

      {/* 404 - redirect in base al ruolo */}
      <Route path="*" element={<RoleBasedRedirect />} />
    </Routes>
  );
}

export default App;
